import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, PUT } from 'helpers/api'
import { getTableSelectProps } from 'helpers/helpers'
import HintIcon from 'pages/parts/HintIcon'

// Таблица точек канала: включение точки плюс переопределение меню и ценовой категории
// для конкретного филиала. Канал задаёт витрину бренда, филиал — исключение из неё.
//
// Пишет НЕ в таблицу канала, а в расписание витрин (menu_schedules) — туда же, куда
// пишут карточка точки и вкладка «Точки» в карточке меню. Одна настройка, три экрана:
// иначе один филиал пришлось бы настраивать отдельно в боте, на сайте и в приложении.

export const CHANNEL_POS_MAIN_MENU = -1

// Селекты встроены в ячейки, а список рисуется в body: у table-responsive свой
// overflow, и внутри таблицы список обрезался по высоте строки.
const SELECT_PORTAL_PROPS = getTableSelectProps()

/** Сохранить переопределения точек. Зовётся после успешного сохранения канала. */
export async function saveChannelPosesMenu(overrides) {
	const rows = Object.entries(overrides ?? {})

	for (const [posId, value] of rows) {
		if (!value?.dirty) continue

		await PUT('/menu-schedules/pos', {
			pos_id: Number(posId),
			external_menu_id: value.external_menu_id > 0 ? value.external_menu_id : null,
			use_main_menu: value.external_menu_id === CHANNEL_POS_MAIN_MENU,
			price_category_id: value.price_category_id ?? null,
		}, { notification: false })
	}
}

function ChannelPosesMenu({ poses = [], overrides = {}, setOverrides, onToggle }) {
	const { t } = useTranslation()

	const [menus, setMenus] = useState([])
	const [priceCategories, setPriceCategories] = useState([])

	// Пустое поле = «как в канале»: у филиала нет своего выбора, он берёт меню и цены,
	// выбранные выше в конструкторе. Отдельным пунктом списка это не объяснить короче,
	// чем пустотой, поэтому селекты просто очищаемые.
	const menuOptions = [
		{ id: CHANNEL_POS_MAIN_MENU, name: t('pos_default_menu_main') },
		...menus,
	]
	const categoryOptions = priceCategories

	async function getData() {
		const menusResponse = await GET('/external-groups', { size: 100 })
		if (httpOk(menusResponse)) {
			setMenus((menusResponse.data?.data ?? []).map(item => ({ id: item.id, name: item.name })))
		}

		const categoriesResponse = await GET('/price-categories')
		if (httpOk(categoriesResponse)) setPriceCategories(categoriesResponse.data?.data ?? [])

		// Текущие переопределения филиалов — строки расписания без периода
		const schedules = await GET('/menu-schedules')
		if (httpOk(schedules)) {
			const next = {}
			for (const row of (schedules.data?.data ?? [])) {
				if (row.price_period_id) continue

				next[row.pos_id] = {
					external_menu_id: row.external_menu_id
						? Number(row.external_menu_id)
						: (row.is_main_menu ? CHANNEL_POS_MAIN_MENU : 0),
					price_category_id: row.price_category_id ? Number(row.price_category_id) : 0,
					dirty: false,
				}
			}
			setOverrides(next)
		}
	}

	function changeOverride(posId, field, value) {
		const current = overrides[posId] ?? { external_menu_id: 0, price_category_id: 0 }
		setOverrides({
			...overrides,
			[posId]: { ...current, [field]: value, dirty: true },
		})
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="table-responsive">
				<table className="table table-bordered mb-0">
					<thead>
						<tr>
							<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
							<th style={{ 'width': '30%' }}>{t('name2')}</th>
							<th style={{ 'width': '30%' }}>{t('menu')}<HintIcon text={t('channel_pos_menu_hint')} /></th>
							<th style={{ 'width': '30%' }}>{t('price_category')}</th>
							<th style={{ 'width': '10%' }}></th>
						</tr>
					</thead>
					<tbody>
						{poses.map((item, index) => (
							<tr key={index}>
								<td style={{ 'width': '33px' }} className="text-center vertical-middle">{index + 1}</td>
								<td className="td-ellipsis vertical-middle">{item.name}</td>
								<td className="td-select">
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={menuOptions}
										value={menuOptions.find(option => option.id === overrides[item.id]?.external_menu_id) || ''}
										onChange={(option) => changeOverride(item.id, 'external_menu_id', option ? option.id : 0)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id} />
								</td>
								<td className="td-select">
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={categoryOptions}
										value={categoryOptions.find(option => option.id === overrides[item.id]?.price_category_id) || ''}
										onChange={(option) => changeOverride(item.id, 'price_category_id', option ? option.id : 0)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id} />
								</td>
								<td className="d-flex justify-content-center">
									<div className="form-check form-switch form-switch-sm">
										<input type="checkbox" className="form-check-input"
											checked={item.status ? true : false}
											onChange={(e) => onToggle(e, index)} />
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	)
}

export default ChannelPosesMenu
